import PublicSiteLayout from "@/components/layouts/PublicSiteLayout";
import LandingPricing from "@/components/marketing/LandingPricing";
import Seo from "@/components/marketing/Seo";
import BrandWordmark from "@/components/marketing/BrandWordmark";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, CheckCircle2, CircleHelp, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export interface SeoLandingLink {
  title: string;
  path: string;
  description: string;
}

export interface SeoLandingFaq {
  question: string;
  answer: string;
}

export interface SeoLandingPageData {
  path: string;
  title: string;
  seoTitle: string;
  seoDescription: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroSubhead: string;
  heroPrimaryCta: string;
  heroPrimaryHref?: string;
  heroPanelEyebrow: string;
  heroPanelTitle: string;
  heroPanelBullets: string[];
  heroPanelFooter?: string;
  frameworkTitle: string;
  frameworkBullets: string[];
  frameworkFooter?: string;
  exampleTitle: string;
  exampleBlocks: string[];
  exampleFooter?: string;
  pressureTitle?: string;
  pressureQuote?: string;
  pressureBody?: string;
  pressureFooter?: string;
  pressureFooterHighlight?: string;
  pressureBackgroundPhrases?: string[];
  mistakesTitle: string;
  mistakes: string[];
  mistakesFooter?: string;
  ctaEyebrow: string;
  ctaTitle: string;
  ctaBullets: string[];
  ctaFooter?: string;
  ctaInfoCard: SeoLandingLink;
  ctaActionTitle: string;
  ctaActionDescription: string;
  ctaActionHref?: string;
  relatedTitle: string;
  relatedSubtext?: string;
  relatedLinks: SeoLandingLink[];
  faqs: SeoLandingFaq[];
  pricingEyebrow?: string;
  pricingTitle?: string;
  pricingDescription?: string;
}

const renderHighlightedFooter = (footer: string, highlight?: string) => {
  if (!highlight || !footer.includes(highlight)) {
    return footer;
  }

  const [before, after] = footer.split(highlight);
  return (
    <>
      {before}
      <span className="font-bold text-rose-600">{highlight}</span>
      {after}
    </>
  );
};

const SeoLandingTemplate = ({ page }: { page: SeoLandingPageData }) => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: page.title,
      description: page.seoDescription,
      url: `https://hiredcoach.ai${page.path}`,
      isPartOf: {
        "@type": "WebSite",
        name: "HiredCoach",
        url: "https://hiredcoach.ai/",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <PublicSiteLayout>
      <Seo
        title={page.seoTitle}
        description={page.seoDescription}
        canonicalPath={page.path}
        keywords={`interview prep, ${page.title.toLowerCase()}, coach u, hiredcoach`}
        structuredData={structuredData}
      />

      <section className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(79,158,203,0.24),_transparent_34%),linear-gradient(180deg,#f8fbff_0%,#fff7ea_100%)]">
        <div className="container mx-auto px-6 py-14 md:py-20">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
                {page.heroEyebrow}
              </p>
              <h1 className="mt-5 text-4xl font-bold tracking-tight text-[#334763] md:text-5xl/[1.05]">
                {page.heroHeadline}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">
                {page.heroSubhead.replace(/\n+/g, " ")}
              </p>
              <div className="mt-8 flex justify-center">
                <Button asChild className="h-14 rounded-full bg-sky-600 px-8 text-base font-semibold text-white hover:bg-sky-500">
                  <Link to={page.heroPrimaryHref || "/register"}>
                    {page.heroPrimaryCta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <Card className="rounded-[2rem] border border-sky-200 bg-white/90 shadow-xl shadow-sky-100">
              <CardContent className="p-7 md:p-8">
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-800">
                  <Sparkles className="h-4 w-4" />
                  {page.heroPanelEyebrow}
                </div>
                <div className="mt-5 space-y-5">
                  <p className="text-2xl font-bold leading-8 text-slate-900">{page.heroPanelTitle}</p>
                  <div className="space-y-3">
                    {page.heroPanelBullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-3 rounded-2xl bg-[#f8fbff] p-4">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                        <p className="text-base font-semibold leading-7 text-slate-700">{bullet}</p>
                      </div>
                    ))}
                  </div>
                  {page.heroPanelFooter && (
                    <p className="text-lg font-semibold leading-8 text-slate-900">{page.heroPanelFooter}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="container mx-auto grid gap-6 px-6 lg:grid-cols-2">
          <Card className="rounded-[2rem] border border-slate-200 shadow-sm">
            <CardContent className="p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">
                {page.frameworkTitle}
              </p>
              <div className="mt-5 space-y-4">
                {page.frameworkBullets.map((bullet) => (
                  <div key={bullet} className="flex gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                    <p className="text-base leading-7 text-slate-700">{bullet}</p>
                  </div>
                ))}
              </div>
              {page.frameworkFooter && (
                <p className="mt-6 text-base leading-8 text-slate-700">{page.frameworkFooter}</p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[2rem] border border-slate-200 bg-[#fffaf2] shadow-sm">
            <CardContent className="p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#b96b24]">
                {page.exampleTitle}
              </p>
              <div className="mt-5 space-y-4">
                {page.exampleBlocks.map((block) => (
                  <div key={block} className="rounded-2xl bg-white/80 p-5 shadow-sm">
                    <p className="text-base leading-8 text-slate-700">{block}</p>
                  </div>
                ))}
              </div>
              {page.exampleFooter && (
                <p className="mt-5 text-base leading-8 text-slate-700">{page.exampleFooter}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {page.pressureTitle && (
          <div className="container mx-auto mt-8 px-6">
            <Card className="overflow-hidden rounded-[2rem] border border-rose-200 bg-rose-50 shadow-sm">
              <CardContent className="relative p-7 md:p-8">
                {page.pressureBackgroundPhrases && (
                  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden opacity-25">
                    <div className="absolute inset-x-6 inset-y-5 grid gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.28em] text-rose-300">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <div
                          key={`${page.pressureBackgroundPhrases?.[index % page.pressureBackgroundPhrases.length]}-${index}`}
                          className={index % 2 === 0 ? "" : "justify-self-end"}
                        >
                          {page.pressureBackgroundPhrases?.[index % page.pressureBackgroundPhrases.length]}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="relative flex flex-col gap-3">
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-rose-700">
                    {page.pressureTitle}
                  </p>
                  {page.pressureQuote && (
                    <p className="text-lg font-semibold leading-8 text-slate-900">{page.pressureQuote}</p>
                  )}
                  {page.pressureBody && (
                    <p className="max-w-3xl text-lg font-semibold leading-8 text-slate-900">{page.pressureBody}</p>
                  )}
                  {page.pressureFooter && (
                    <p className="text-center text-2xl font-semibold leading-9 text-slate-900">
                      {renderHighlightedFooter(page.pressureFooter, page.pressureFooterHighlight)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </section>

      <section className="bg-[#f8fbff] py-16">
        <div className="container mx-auto grid gap-6 px-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Card className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <CardContent className="p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-rose-700">
                {page.mistakesTitle}
              </p>
              <div className="mt-5 space-y-4">
                {page.mistakes.map((mistake) => (
                  <div key={mistake} className="flex gap-3">
                    <CircleHelp className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
                    <p className="text-base leading-7 text-slate-700">{mistake}</p>
                  </div>
                ))}
              </div>
              {page.mistakesFooter && (
                <p className="mt-6 text-base font-semibold leading-8 text-slate-900">{page.mistakesFooter}</p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-[#243247] to-[#334763] text-white shadow-xl shadow-slate-200">
            <CardContent className="p-7 md:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-200">
                {page.ctaEyebrow}
              </p>
              <h2 className="mt-4 text-3xl font-bold">{page.ctaTitle}</h2>
              <div className="mt-5 space-y-3 text-lg leading-8 text-slate-100">
                {page.ctaBullets.map((bullet) => (
                  <div key={bullet} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-sky-300" />
                    <p>{bullet}</p>
                  </div>
                ))}
              </div>
              {page.ctaFooter && (
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-200">{page.ctaFooter}</p>
              )}
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                <Link
                  to={page.ctaInfoCard.path}
                  className="rounded-[1.5rem] border border-white/10 bg-white/10 p-5 transition hover:bg-white/15"
                >
                  <p className="text-lg font-semibold">{page.ctaInfoCard.title}</p>
                  <p className="mt-2 text-sm leading-7 text-slate-200">{page.ctaInfoCard.description}</p>
                </Link>
                <Link
                  to={page.ctaActionHref || "/register"}
                  className="rounded-[1.5rem] border border-sky-300/40 bg-sky-500/20 p-5 transition hover:bg-sky-500/30"
                >
                  <p className="text-lg font-semibold">{page.ctaActionTitle}</p>
                  <p className="mt-2 text-sm leading-7 text-slate-100">{page.ctaActionDescription}</p>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
              Keep going
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">
              {page.relatedTitle}
            </h2>
            {page.relatedSubtext && (
              <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">{page.relatedSubtext}</p>
            )}
            <div className="mt-6 grid gap-4">
              {page.relatedLinks.map((related) => (
                <Link
                  key={related.path}
                  to={related.path}
                  className="rounded-[1.75rem] border border-slate-200 bg-[#fffaf2] p-5 transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-100"
                >
                  <p className="text-lg font-semibold text-slate-900">{related.title}</p>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{related.description}</p>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#b96b24]">
              Quick answers
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">
              The stuff people usually ask next
            </h2>
            <div className="mt-6 space-y-4">
              {page.faqs.map((faq) => (
                <Card key={faq.question} className="rounded-[1.5rem] border border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <p className="text-lg font-semibold text-slate-900">{faq.question}</p>
                    <p className="mt-2 whitespace-pre-line text-sm leading-7 text-slate-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingPricing
        eyebrow={page.pricingEyebrow || "Practice makes perfect"}
        title={
          page.pricingTitle || (
            <>
              Ace that interview with <BrandWordmark />&apos;s help
            </>
          )
        }
        description={
          page.pricingDescription || (
            <>
              <BrandWordmark /> is the personal AI job hunting coach you deserve.
            </>
          )
        }
      />
    </PublicSiteLayout>
  );
};

export default SeoLandingTemplate;
